import { NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, map } from 'rxjs';

import { ResizeObserverService } from '@core/services/resize-observer.service';

import { EvseStatusStoreModelEnum, VehicleOperationalStatusStoreModelEnum } from '@domain/single-site/single-site.enums';

import { TranslateEvseStatusCapitalizedPipe } from '@shared/pipes/translate-enum-pipes/translate-evse-status-capitalized.pipe';

import { SitesTableEntitiesPerStateCellPartComponent } from '../sites-table/components/sites-table-entities-per-state-cell-part';

@Component({
  selector: 'app-sites-table-evses-per-state-cell',
  imports: [SitesTableEntitiesPerStateCellPartComponent, NgStyle, TranslateEvseStatusCapitalizedPipe],
  templateUrl: './sites-table-evses-per-state-cell.component.html',
  styleUrl: './sites-table-evses-per-state-cell.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SitesTableEvsesPerStateCellComponent {
  readonly #elementRef = inject(ElementRef);
  readonly #resizeObserverService = inject(ResizeObserverService);

  public evsesPerState = input.required<Partial<Record<EvseStatusStoreModelEnum, number>>>();
  public widthPx$ = toSignal(
    this.#resizeObserverService.observe$(this.#elementRef.nativeElement).pipe(
      map(() => this.#elementRef.nativeElement.getBoundingClientRect().width),
      distinctUntilChanged()
    )
  );

  public parts = computed(() => {
    const evsesPerState = this.evsesPerState();
    const widthPx = this.widthPx$();
    if (!widthPx) {
      return [];
    }
    const totalEvses = Object.values(evsesPerState).reduce((acc, value) => acc + value, 0);
    const allPartsCount = Object.values(evsesPerState).reduce((acc, value) => {
      if (value > 0) {
        return acc + 1;
      }
      return acc;
    }, 0);

    const parts: {
      count: number;
      bgColorClass: string;
      width: number;
      status: EvseStatusStoreModelEnum;
    }[] = [];

    const minPartWidth = 9;
    const gapSize = 2;
    const minimumRequiredSpace = allPartsCount * minPartWidth + (allPartsCount - 1) * gapSize;
    const remainingSpace = Math.max(widthPx - minimumRequiredSpace, 0);

    if (evsesPerState[EvseStatusStoreModelEnum.Charging]) {
      const count = evsesPerState[EvseStatusStoreModelEnum.Charging];
      parts.push({
        count,
        bgColorClass: 'bg-chargingstatus-charging-100',
        width: minPartWidth + (count / totalEvses) * remainingSpace,
        status: EvseStatusStoreModelEnum.Charging,
      });
    }

    if (evsesPerState[EvseStatusStoreModelEnum.Queued]) {
      const count = evsesPerState[EvseStatusStoreModelEnum.Queued];
      parts.push({
        count,
        bgColorClass: 'bg-chargingstatus-queued-100',
        width: minPartWidth + (count / totalEvses) * remainingSpace,
        status: EvseStatusStoreModelEnum.Queued,
      });
    }

    if (evsesPerState[EvseStatusStoreModelEnum.Available]) {
      const count = evsesPerState[EvseStatusStoreModelEnum.Available];
      parts.push({
        count,
        bgColorClass: 'bg-status-success',
        width: minPartWidth + (count / totalEvses) * remainingSpace,
        status: EvseStatusStoreModelEnum.Available,
      });
    }

    if (evsesPerState[EvseStatusStoreModelEnum.Faulted]) {
      const count = evsesPerState[EvseStatusStoreModelEnum.Faulted];
      parts.push({
        count,
        bgColorClass: 'bg-chargingstatus-faulted-100',
        width: minPartWidth + (count / totalEvses) * remainingSpace,
        status: EvseStatusStoreModelEnum.Faulted,
      });
    }

    if (evsesPerState[EvseStatusStoreModelEnum.Maintenance]) {
      const count = evsesPerState[EvseStatusStoreModelEnum.Maintenance];
      parts.push({
        count,
        bgColorClass: 'bg-status-warning',
        width: minPartWidth + (count / totalEvses) * remainingSpace,
        status: EvseStatusStoreModelEnum.Maintenance,
      });
    }

    if (evsesPerState[EvseStatusStoreModelEnum.Unknown]) {
      const count = evsesPerState[EvseStatusStoreModelEnum.Unknown];
      parts.push({
        count,
        bgColorClass: 'bg-chargingstatus-unknown-100',
        width: minPartWidth + (count / totalEvses) * remainingSpace,
        status: EvseStatusStoreModelEnum.Unknown,
      });
    }

    return parts;
  });

  public readonly VehicleOperationalStatusStoreModelEnum = VehicleOperationalStatusStoreModelEnum;
}
